import { Ref, forwardRef, useImperativeHandle } from "react";
import { FieldArrayWithId, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ReportScheduleRecipientType, ScheduledReportsFormType } from "../../ScheduledReportsEditorUtils";
import { Box, Stack } from "@mui/material";
import { ScheduledReportsRecipientListItem } from "./ScheduledReportsRecipientListItem";
import ScheduledReportsNotificationUtils, { RecipientActions } from "../ScheduledReportsNotificationUtils";
import AddEmailDialog, { useAddEmailDialogState, bindAddEmailDialog } from "../../add-email-dialog/AddEmailDialog";
import { eLynxObjectTypeEnum } from "../../../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../../../common/utilities/GetObjectTypeName";
import { useUserContext } from "../../../../contexts/user-context/UserContext";
import { hiddenRecipients } from "../../../../services/reporting-data-service/ReportScheduleModels";
import { BaseActions } from "../../../action-menu/ActionMenuItem";
import NoData from "../../../no-data/NoData";
import VirtualizedList from "../../../virtualized-list/VirtualizedList";
import { contains } from "../../../../common/utilities/Contains";

export type ScheduledReportsRecipientListProps = {
    visible?: boolean;
    viewSelected: boolean;
    filterText: string;
    organizationID: number | undefined,
};

export type ScheduledReportsRecipientListType = {
    executeAction: (action: RecipientActions | BaseActions) => void;
};

function ScheduledReportsRecipientList({
    visible,
    viewSelected,
    filterText,
    organizationID,
}: ScheduledReportsRecipientListProps, ref: Ref<unknown> | undefined) {

    const [userState] = useUserContext();
    const {t} = useTranslation();
   
    const addEmailDialog = useAddEmailDialogState();

    useImperativeHandle<unknown, ScheduledReportsRecipientListType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const {
        //watch,
        control,
    } = useFormContext<ScheduledReportsFormType>();    

    const {
        fields,
        insert,
        //append,
        //remove:
        update
    } = useFieldArray({
        control: control,
        name: 'recipients',
        keyName: "fieldKey"
    });    

    const recipients = fields.filter(o => (!hiddenRecipients.find(v => v === o.address) || userState.user?.isElynxAdmin))
    const count = ScheduledReportsNotificationUtils.filterRecipients(recipients, filterText, organizationID, viewSelected).length;

    async function onAddEmail() {
        const result = await addEmailDialog.open();

        if (result.reason === "ok") {


            const emailAddress = result.values?.emailAddress;

            const ndx = fields.findIndex(o => o.address.toLowerCase() === emailAddress?.toLowerCase());

            if (ndx !== -1) {
                const item = fields[ndx];
                update(ndx, {
                    ...item, 
                    selected: true
                });
            } else {
                insert(0, {
                        id: -1,
                        address: emailAddress,
                        organizationID: -1,
                        selected: true
                    } as ReportScheduleRecipientType
                );
            }
        }
    }

    function executeAction(action: RecipientActions | BaseActions) {

        switch(action) {
            case RecipientActions.Add:
                onAddEmail();
                break;
        }
    }

    function renderItem(_index: number, item: FieldArrayWithId<ScheduledReportsFormType, 'recipients', 'fieldKey'>) {
        return (
            <ScheduledReportsRecipientListItem
                item={item}
                key={item.fieldKey}
            />
        );
    };


    function onFilterItem(item: FieldArrayWithId<ScheduledReportsFormType, 'recipients', 'fieldKey'>): boolean {

        let show = !!contains(item.address, filterText);

        if (show && organizationID) {
            show = item.organizationID === organizationID;
        }

        if (show && viewSelected) {
            show = item.selected;
        }

        return show;
    }

    return (visible ? <Stack
        direction='row'
        sx={{
            display: 'flex',
            flex: '1 1 auto'
        }}>
        <Box
            sx={{
                display: 'flex',
                flex: '1 1 auto',
                flexDirection: 'column',
                overflowY: 'auto'
            }}>
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                overflow: "hidden"
                }}>

                {count === 0 &&         
                    <Box sx={{
                        padding: 1
                    }}>
                        <NoData text={t('setpointAdmin.nodata.noFilterMatch', {name: getObjectTypeName(eLynxObjectTypeEnum.Recipient, true, true)})} />
                    </Box>
                }
                                        
                <VirtualizedList
                    items={recipients}
                    renderItem={renderItem}
                    filter={onFilterItem}
                />

            </Box>
        </Box>

        <AddEmailDialog {...bindAddEmailDialog(addEmailDialog)} />
    </Stack> : null);
}

export default forwardRef(ScheduledReportsRecipientList);